import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldAlert, Radio, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Authenticated", { description: "Production session established" });
      nav("/", { replace: true });
    } else {
      toast.error("Access Denied", { description: res.error || "Invalid credentials." });
    }
  };

  return (
    <div className="min-h-screen w-full bg-[var(--bg-app)] grid-bg relative overflow-hidden" data-testid="login-page">
      {/* Accent grid lines */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-white/10" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-white/10" />
      </div>

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] min-h-screen">
        {/* Left side - branding */}
        <div className="hidden lg:flex flex-col justify-between p-10 border-r border-white/10">
            <img src="/logo.png" alt="CIMAP" className="w-9 h-9 object-contain" />
            <div>
              <div className="font-display text-sm tracking-widest uppercase">CIMAP</div>
              <div className="label-micro mt-0.5">Corporate Intelligence · Monitor · Assess · Predict</div>
            </div>

            <div>
              <div className="label-micro mb-4">SYSTEM STATUS</div>
              <h1 className="font-display text-5xl xl:text-6xl leading-[0.95] tracking-tight">
                REAL-TIME<br />
                <span className="text-white/60">INTELLIGENCE</span><br />
                COMMAND CENTER
              </h1>
              <p className="mt-6 text-sm text-white/60 max-w-md leading-relaxed">
                Autonomous monitoring of public perception, risks, and emerging threats across
                the global digital ecosystem — delivering 100% real-time verified data.
              </p>

              <div className="mt-10 grid grid-cols-3 gap-px bg-white/10">
                {[
                  { k: "Signal Stream", v: "LIVE" },
                  { k: "AI Engine", v: "ACTIVE" },
                  { k: "Uplink", v: "SECURE" },
                ].map((s) => (
                  <div key={s.k} className="bg-[var(--bg-app)] p-4">
                    <div className="label-micro">{s.k}</div>
                    <div className="mt-1 font-display text-2xl">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-white/40">
              <Radio className="w-3.5 h-3.5 text-[var(--sev-low)] pulse-dot" />
              <span>Connected to Adani CIMAP Production Uplink</span>
            </div>
        </div>

        {/* Right side - auth */}
        <div className="flex items-center justify-center p-6 lg:p-10">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <img src="/logo.png" alt="CIMAP" className="w-9 h-9 object-contain" />
              <div className="font-display text-sm tracking-widest uppercase">CIMAP</div>
            </div>

            <div className="label-micro mb-2">Production Access</div>
            <h2 className="font-display text-3xl tracking-tight">Operator Login</h2>
            <p className="mt-2 text-xs text-white/50">
              Enter your credentials to access the live dashboard.
            </p>

            <form onSubmit={handleLogin} className="mt-8 space-y-4">
              <div>
                <label className="label-micro block mb-1.5">Username / Email</label>
                <Input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="operator@adani.local"
                  className="bg-[var(--bg-surface)] border-white/15 text-white h-11 rounded-none"
                />
              </div>
              <div>
                <label className="label-micro block mb-1.5">Password</label>
                <Input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-[var(--bg-surface)] border-white/15 text-white h-11 rounded-none"
                />
              </div>
              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="w-full h-11 rounded-none bg-accent-strong hover:bg-accent-strong font-medium tracking-wide"
              >
                {loading ? "Authenticating..." : "Access Dashboard"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
            
            <div className="mt-12 p-4 border border-white/5 bg-white/[0.02]">
              <div className="label-micro text-[var(--sev-high)] flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[var(--sev-high)]" />
                Security Notice
              </div>
              <p className="mt-2 text-[10px] text-white/40 leading-relaxed uppercase tracking-wider">
                This system contains sensitive real-time intelligence data. 
                All access and commands are logged for security auditing.
                Unauthorized access is strictly prohibited.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
